import React, { useCallback, useMemo } from 'react';
import styles from './ClientRosterList.module.css';
import { useApexInfluencersData } from '../../../influencer/roster/RosterList/useApexInfluencerListData';
import RosterInfluencerBar from '../../../influencer/roster/RosterList/components/RosterInfluencerBar/RosterInfluencerBar';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import { ApexInfluencer, encodeUrlSearchParams, InstagramUserImage, Platform, TiktokUserImage } from '@round/api';
import useOnScroll from '../../../../hooks/useOnScroll';
import { Skeleton } from '@round/ui-kit';
import SearchInput from '../../../../ui/SearchInput/SearchInput';
import { campaignTypeOptions } from '../../../../utils/constants';
import { useInfluencerTags } from '../../../../hooks/useInfluencerTags';
import { ClientBookingDispatchContext } from '../../booking/ClientBookingContext/ClientBookingContext';
import BookingDetailsDropdown from '../../booking/containers/BookingDetailsDropdown/BookingDetailsDropdown';
import useUrlState from '../../../../hooks/useUrlState';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '../../../../utils/showNotification';
import RosterFilters, {
    RosterFiltersObject,
} from '../../../influencer/roster/RosterList/components/RosterFilters/RosterFilters';
import range from 'lodash/range';
import { parseConditionsFromUrlString } from '../../../influencer/roster/RosterList/components/RosterFilters/CategoriesFilter/CategoriesFilter.helpers';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import { useContentTags } from '../../../../contexts/ContentTagsContext';

type RosterListRow = {
    influencer: ApexInfluencer;
    influencerImage: TiktokUserImage | InstagramUserImage | undefined;
};

const ClientRosterList = () => {
    const dispatch = useNonNullContext(ClientBookingDispatchContext);
    const navigate = useNavigate();
    const { currencies } = useNonNullContext(OptionsContext);
    const { influencerTags } = useInfluencerTags();

    const { tags: contentTags, isInitialized: isContentTagsInitialized, init: initContentTags } = useContentTags();

    useAbortableEffect(
        (signal) => {
            if (!isContentTagsInitialized) {
                initContentTags({ signal });
            }
        },
        [isContentTagsInitialized, initContentTags]
    );

    const [urlState, setUrlState] = useUrlState<
        RosterFiltersObject & {
            search: string;
            tiktok_influencer_ids: string | undefined;
            instagram_influencer_ids: string | undefined;
        }
    >(
        {
            search: '',
            min_cost: 50,
            campaign_type: 'AUDIO',
        },
        {
            platforms: 'array-string',
            currency: 'number',
            categories: 'array-number',
            locations: 'array-number',
            min_followers: 'number',
            max_followers: 'number',
            min_cost: 'number',
            max_cost: 'number',
        }
    );

    const selectedTiktokInfluencerIds = useMemo(
        () => urlState.tiktok_influencer_ids?.split(',').map((id) => Number(id)),
        [urlState.tiktok_influencer_ids]
    );
    const selectedInstagramInfluencerIds = useMemo(
        () => urlState.instagram_influencer_ids?.split(',').map((id) => Number(id)),
        [urlState.instagram_influencer_ids]
    );

    const tagsParam = useMemo(
        () =>
            parseConditionsFromUrlString(urlState.content_tags).reduce((acc, item) => {
                if (item.type === 'all') {
                    return acc.concat(item.value.map((tag) => [tag]));
                }

                return acc.concat([item.value]);
            }, [] as number[][]),
        [urlState.content_tags]
    );

    const {
        influencersData,
        page,
        hasNextInfluencersPage,
        initialized,
        influencersLoading,
        errorLoadingInfluencers,
        tiktokUserImages,
        instagramUserImages,
        loadNextPage,
    } = useApexInfluencersData({
        currency: urlState.currency,
        campaign_type: urlState.campaign_type,
        search: urlState.search,
        platforms: urlState.platforms?.join(','),
        category_tags: urlState.categories?.join(','),
        location_tags: urlState.locations?.join(','),
        min_followers: urlState.min_followers ? `${urlState.min_followers}` : undefined,
        max_followers: urlState.max_followers ? `${urlState.max_followers}` : undefined,
        min_cost: urlState.min_cost ? `${urlState.min_cost}` : undefined,
        max_cost: urlState.max_cost ? `${urlState.max_cost}` : undefined,
        content_tags: tagsParam,
    });

    const nextPageLoading = initialized && influencersLoading;
    const bottomReached = initialized && page > 1 && !hasNextInfluencersPage;

    const loadNextRows = () => {
        if (influencersLoading || errorLoadingInfluencers || !hasNextInfluencersPage || !initialized) {
            return;
        }
        loadNextPage().catch(() => showNotification('Could not load next page', 'error'));
    };

    const handleScroll = useOnScroll(loadNextRows, { threshold: 600 });

    const addToBooking = useCallback(
        (platform: Platform, influencer: ApexInfluencer) => {
            if (platform === 'TIKTOK') {
                if (selectedTiktokInfluencerIds?.includes(influencer.id)) {
                    return;
                }
                setUrlState({
                    tiktok_influencer_ids: selectedTiktokInfluencerIds
                        ? selectedTiktokInfluencerIds?.concat(influencer.id).join()
                        : `${influencer.id}`,
                });
            } else if (platform === 'INSTAGRAM') {
                if (selectedInstagramInfluencerIds?.includes(influencer.id)) {
                    return;
                }
                setUrlState({
                    instagram_influencer_ids: selectedInstagramInfluencerIds
                        ? selectedInstagramInfluencerIds?.concat(influencer.id).join()
                        : `${influencer.id}`,
                });
            }
            const influencerImage =
                tiktokUserImages.find((img) => img.user_id === influencer.tiktok_user_id) ||
                instagramUserImages.find((img) => img.user_id === influencer.instagram_user_id);
            if (influencerImage) {
                dispatch({
                    type: 'addInfluencerToBooking',
                    payload: {
                        influencer: influencer,
                        influencerImage: {
                            influencerId: influencer.id,
                            image: influencerImage,
                        },
                    },
                });
            } else {
                dispatch({ type: 'addInfluencerToBooking', payload: { influencer: influencer } });
            }
        },
        [
            dispatch,
            setUrlState,
            selectedTiktokInfluencerIds,
            selectedInstagramInfluencerIds,
            instagramUserImages,
            tiktokUserImages,
        ]
    );

    const removeFromBooking = useCallback(
        (platform: Platform, influencerId: number | undefined) => {
            if (!influencerId) {
                return;
            }
            if (platform === 'TIKTOK') {
                setUrlState({
                    tiktok_influencer_ids: selectedTiktokInfluencerIds
                        ?.filter((id) => Number(id) !== influencerId)
                        .join(),
                });
            } else if (platform === 'INSTAGRAM') {
                setUrlState({
                    instagram_influencer_ids: selectedInstagramInfluencerIds
                        ?.filter((id) => Number(id) !== influencerId)
                        .join(),
                });
            }
        },
        [setUrlState, selectedInstagramInfluencerIds, selectedTiktokInfluencerIds]
    );

    const createBooking = useCallback(() => {
        const { tiktok_influencer_ids, instagram_influencer_ids, currency, campaign_type } = urlState;
        const url = `/client/new-booking/${encodeUrlSearchParams({
            tiktok_influencer_ids,
            instagram_influencer_ids,
            currency,
            campaign_type,
        })}`;
        navigate(url);
    }, [urlState, navigate]);

    const rosterListRows: RosterListRow[] = useMemo(
        () =>
            influencersData.map((influencer) => ({
                influencer,
                influencerImage:
                    tiktokUserImages.find((img) => img.user_id === influencer.tiktok_user_id) ||
                    instagramUserImages.find((img) => img.user_id === influencer.instagram_user_id),
            })),
        [influencersData, instagramUserImages, tiktokUserImages]
    );

    const bookingCartHasItems = !!urlState.instagram_influencer_ids || !!urlState.tiktok_influencer_ids;

    return (
        <div className={styles.container} onScroll={handleScroll}>
            <div className={styles.headerBar}>
                <SearchInput
                    value={urlState.search}
                    onChange={(value) => {
                        setUrlState({ search: value });
                    }}
                    className={styles.searchBar}
                    placeholder="Search by username"
                />
                <div className={styles.optionsBar}>
                    <RosterFilters
                        filters={{
                            platforms: urlState.platforms,
                            categories: urlState.categories,
                            locations: urlState.locations,
                            min_followers: urlState.min_followers,
                            max_followers: urlState.max_followers,
                            max_cost: urlState.max_cost,
                            min_cost: urlState.min_cost,
                            campaign_type: urlState.campaign_type,
                            currency: urlState.currency,
                            content_tags: urlState.content_tags,
                        }}
                        isCrucialFiltersDisabled={bookingCartHasItems}
                        onFilterChange={(val) => setUrlState(val)}
                    />
                    <div className={styles.bookingDetailsContainer}>
                        <div className={styles.bookingDetailsTitleBar}>
                            <span>BOOKING DETAILS</span>
                        </div>
                        <BookingDetailsDropdown
                            influencerBookings={{
                                tiktok: selectedTiktokInfluencerIds || [],
                                instagram: selectedInstagramInfluencerIds || [],
                            }}
                            selectedCurrency={Number(urlState.currency)}
                            campaignType={urlState.campaign_type || campaignTypeOptions[1].value}
                            removeBooking={removeFromBooking}
                            createBooking={createBooking}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.list}>
                {rosterListRows.map((row) => {
                    const bookings = [
                        ...(selectedTiktokInfluencerIds?.includes(row.influencer.id) ? ['TIKTOK'] : []),
                        ...(selectedInstagramInfluencerIds?.includes(row.influencer.id) ? ['INSTAGRAM'] : []),
                    ] as Platform[];
                    return (
                        <RosterInfluencerBar
                            key={row.influencer.id}
                            influencer={row.influencer}
                            influencerImage={row.influencerImage}
                            locationTag={influencerTags.find((tag) => row.influencer.location === tag.id)}
                            contentTags={contentTags.filter((tag) => row.influencer.content_tags.includes(tag.id))}
                            campaignType={urlState.campaign_type}
                            currency={currencies.find((curr) => curr.id === Number(urlState.currency))}
                            bookings={bookings}
                            addToBooking={addToBooking}
                        />
                    );
                })}
                {(nextPageLoading || !initialized) &&
                    !errorLoadingInfluencers &&
                    range(0, 6).map((i) => (
                        <Skeleton
                            key={i}
                            className={styles.barSkeleton}
                            containerClassName={styles.skeletonContainer}
                        />
                    ))}
                {bottomReached && (
                    <div className={styles.bottomReachedBar}>
                        <h4>Looks like you've reached the bottom</h4>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientRosterList;
